import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ShieldAlert, Clock, Mail, Loader2, ShieldCheck } from "lucide-react";
import { useApp, API } from "../context";
import { toast } from "sonner";

/**
 * Global parental-consent enforcement.
 *
 * States (from GET /parental-consent/status/{user_id}):
 *  - not_required / approved: render nothing (no banner, no gate)
 *  - pending (within grace): render a sticky amber banner at top ("Parent approval pending · 42h left")
 *  - expired / denied: render a FULL-PAGE blocking gate with resend button; children are hidden
 *
 * Usage: mount inside AppRouter / main layout and wrap nothing, it decides itself.
 */
const ParentalConsentGate = ({ children }) => {
  const { user } = useApp();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!user || user.isDemo) { setStatus(null); return; }
    const uid = user.id || user.email;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/parental-consent/status/${uid}`);
      setStatus(r.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Periodic recheck in case parent approves while kid is in app
  useEffect(() => {
    if (!user || user.isDemo) return;
    const t = setInterval(fetchStatus, 60_000);
    return () => clearInterval(t);
  }, [user, fetchStatus]);

  const resend = async () => {
    if (!user) return;
    setResending(true);
    try {
      await axios.post(`${API}/parental-consent/resend`, { user_id: user.id || user.email });
      toast.success("A fresh approval email is on the way to your parent!");
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't resend. Try again in a minute.");
    }
    setResending(false);
  };

  // No user / not required / approved, render children as normal
  if (!status || !status.required || status.status === "approved") {
    return <>{children}</>;
  }

  // PENDING with time left, sticky banner only; app stays usable
  if (status.status === "pending" && !status.locked) {
    const hours = status.hours_left ?? 0;
    const label = hours >= 1 ? `${hours}h left` : "less than an hour left";
    return (
      <>
        <div
          className="sticky top-0 z-[60] w-full border-b border-amber-400/30 bg-gradient-to-r from-amber-500/95 to-orange-500/95 text-amber-950"
          data-testid="parental-consent-banner"
        >
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-3 text-xs sm:text-sm">
            <ShieldAlert size={16} className="flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <b>Parent approval pending</b>
              <span className="hidden sm:inline">, we emailed <b>{status.parent_email}</b> and
              they have <b>{label}</b> to approve before your account is paused.</span>
              <span className="inline sm:hidden">, {label}.</span>
            </div>
            <button
              onClick={resend}
              disabled={resending}
              className="px-3 py-1 rounded-full bg-amber-950/15 hover:bg-amber-950/25 border border-amber-950/30 text-amber-950 text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-60"
              data-testid="consent-resend-btn"
            >
              {resending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
              Resend
            </button>
          </div>
        </div>
        {children}
      </>
    );
  }

  // EXPIRED / DENIED / PAUSED, full blocking gate
  const isDenied = status.status === "denied";
  const isPaused = status.status === "paused";
  const headerBg = isPaused ? "from-red-500/40 to-orange-500/40" : "from-amber-500/40 to-orange-500/40";
  const borderColor = isPaused ? "border-red-500/30" : "border-amber-500/30";
  const headline = isPaused ? "Account paused by parent"
                  : isDenied ? "Waiting for your parent"
                  : "Waiting for your parent";
  const subline = isPaused
    ? "A parent on your account pressed Pause. Ask them to unpause from their Parent Dashboard."
    : isDenied
      ? "Your parent denied access. Ask them to reply to the email if they change their mind."
      : "Your 72-hour approval window ended. Ask your parent to approve your account so you can keep playing!";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: "linear-gradient(180deg, #0F172A 0%, #1E1B4B 100%)" }}
      data-testid="parental-consent-gate">
      <div className={`w-full max-w-lg rounded-3xl border-2 ${borderColor} bg-slate-900/95 overflow-hidden`}>
        <div className={`px-7 py-5 bg-gradient-to-r ${headerBg} border-b border-white/10 text-center`}>
          <ShieldCheck size={40} className="text-amber-200 mx-auto mb-2" />
          <h1 className="text-2xl text-white font-fredoka font-bold">{headline}</h1>
          <p className="text-white/80 text-xs mt-2">{subline}</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 text-sm">
            <div className="flex items-center gap-2 text-white/70 mb-1.5">
              <Mail size={13} />
              <span className="text-xs">Email sent to:</span>
            </div>
            <p className="text-white font-bold text-sm break-all">{status.parent_email || "your parent"}</p>
          </div>

          <div className="rounded-xl border border-blue-500/20 bg-blue-500/[0.06] p-4">
            <p className="text-white/80 text-xs font-bold uppercase tracking-wider mb-1.5">What to do</p>
            <ol className="text-white/70 text-xs space-y-1 list-decimal list-inside leading-relaxed">
              <li>Ask your parent to check their email inbox (and spam folder!)</li>
              <li>They&apos;ll see an email from <b>Isaiah&apos;s World</b> with an <b>Approve</b> button</li>
              <li>Once they approve, refresh this page and you&apos;ll be back in the game 🎮</li>
            </ol>
          </div>

          <button
            onClick={resend}
            disabled={resending}
            className="w-full py-3 rounded-2xl font-fredoka font-bold text-sm text-amber-950 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #FBBF24, #F59E0B)" }}
            data-testid="consent-gate-resend-btn"
          >
            {resending ? <Loader2 size={14} className="inline animate-spin mr-2" /> : <Mail size={14} className="inline -mt-0.5 mr-2" />}
            Resend approval email
          </button>
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="w-full py-2.5 rounded-2xl font-fredoka font-bold text-sm text-white/70 bg-white/5 hover:bg-white/10 border border-white/10"
            data-testid="consent-gate-recheck-btn"
          >
            <Clock size={13} className="inline -mt-0.5 mr-2" />
            I&apos;ve been approved, check again
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParentalConsentGate;
